export function doesIntersect(rectA, rectB) {
  return !(
    rectB.left > rectA.left + rectA.width ||
    rectB.left + rectB.width < rectA.left ||
    rectB.top > rectA.top + rectA.height ||
    rectB.top + rectB.height < rectA.top
  );
}

export function getNormalizedRect(pointA, pointB) {
  return {
    top: Math.min(pointA.y, pointB.y),
    left: Math.min(pointA.x, pointB.x),
    width: Math.abs(pointA.x - pointB.x),
    height: Math.abs(pointA.y - pointB.y)
  };
}

export function getIconPosition(icon) {
  return {
    top: icon.y - icon.height / 2,
    bottom: icon.y + icon.height / 2,
    left: icon.x - icon.width / 2,
    right: icon.x + icon.width / 2,
    width: icon.width,
    height: icon.height
  };
}

export function getIconAlignments(referenceIcon, icon) {
  return {
    top: referenceIcon.y - referenceIcon.height / 2 + icon.height / 2,
    bottom: referenceIcon.y + referenceIcon.height / 2 - icon.height / 2,
    left: referenceIcon.x - referenceIcon.width / 2 + icon.width / 2,
    right: referenceIcon.x + referenceIcon.width / 2 - icon.width / 2
  };
}

export function getLeftMostIcon(icons) {
  return icons.reduce((prev, current) => {
    return prev.x < current.x ? prev : current;
  });
}

export function getRightMostIcon(icons) {
  return icons.reduce((prev, current) => {
    return prev.x > current.x ? prev : current;
  });
}

export function getTopMostIcon(icons) {
  return icons.reduce((prev, current) => {
    return prev.y < current.y ? prev : current;
  });
}

export function getBottomMostIcon(icons) {
  return icons.reduce((prev, current) => {
    return prev.y > current.y ? prev : current;
  });
}

export function getMidPoint(icons) {
  const rects = icons.map(getIconPosition);

  const left = Math.min(...rects.map(rect => rect.left));
  const top = Math.min(...rects.map(rect => rect.top));
  const right = Math.max(...rects.map(rect => rect.right));
  const bottom = Math.max(...rects.map(rect => rect.bottom));

  return {x: (left + right) / 2, y: (top + bottom) / 2};
}

export function getSelectedIconArea(icons) {
  const rects = icons.map(getIconPosition);

  const left = Math.min(...rects.map(rect => rect.left));
  const top = Math.min(...rects.map(rect => rect.top));
  const right = Math.max(...rects.map(rect => rect.right));
  const bottom = Math.max(...rects.map(rect => rect.bottom));
  const width = right - left;
  const height = bottom - top;

  return {left, top, width, height};
}

export function getTotalWidth(icons) {
  const totalWidth = getSelectedIconArea(icons).width;
  const totalIconsWidth = icons.reduce((sum, icon) => sum + icon.width, 0);

  return totalWidth - totalIconsWidth;
}

export function getTotalHeight(icons) {
  const totalHeight = getSelectedIconArea(icons).height;
  const totalIconsHeight = icons.reduce((sum, icon) => sum + icon.height, 0);

  return totalHeight - totalIconsHeight;
}

export function getBiggestRect(rects) {
  if (!rects) return null;
  let biggestRect = {
    width: 0,
    height: 0
  };
  rects.forEach(rect => {
    if (
      rect?.width > biggestRect?.width &&
      rect?.height > biggestRect?.height
    ) {
      biggestRect = rect;
    }
  });
  return biggestRect;
}

export function getRectXDelta(rect) {
  if (!rect || isNaN(rect.left) || isNaN(rect.right)) return null;
  return rect.right - rect.left;
}

export function getRectYDelta(rect) {
  if (!rect || isNaN(rect.top) || isNaN(rect.bottom)) return null;
  return rect.bottom - rect.top;
}

export function calculateConstraintsPixelsInches({rect, printScaleFactor}) {
  if (!validConstraintsPixels(rect) || isNaN(printScaleFactor)) {
    return null;
  }
  const constraints = {
    left: rect.left,
    right: rect.right,
    top: rect.top,
    bottom: rect.bottom
  };
  constraints.scaleFactor = printScaleFactor; // It's defined in MMS and is the same for both axis
  constraints.deltaX = getRectXDelta(rect);
  constraints.deltaY = getRectYDelta(rect);
  constraints.deltaXInches = constraints?.deltaX / printScaleFactor;
  constraints.deltaYInches = constraints?.deltaY / printScaleFactor;
  constraints.areaInches =
    constraints?.deltaXInches * constraints?.deltaYInches;
  constraints.center = {
    pixels: {
      x: constraints.left + constraints?.deltaX / 2,
      y: constraints.top + constraints?.deltaY / 2
    },
    inches: {
      x: 0,
      y: 0
    }
  };
  constraints.border = {
    pixels: {
      min: {
        x: constraints.left,
        y: constraints.top
      },
      max: {
        x: constraints.right,
        y: constraints.bottom
      }
    },
    inches: {
      min: {
        x: -(constraints?.deltaXInches / 2),
        y: -(constraints?.deltaYInches / 2)
      },
      max: {
        x: constraints?.deltaXInches / 2,
        y: constraints?.deltaYInches / 2
      }
    }
  };
  return constraints;
}

export const getCoordsInInches = ({
  left,
  top,
  centerCoords,
  printScaleFactor
}) => {
  if (
    isNaN(left) ||
    isNaN(top) ||
    isNaN(centerCoords?.x) ||
    isNaN(centerCoords?.y) ||
    isNaN(printScaleFactor) ||
    printScaleFactor === 0
  ) {
    return null;
  }
  return {
    x: (left - centerCoords.x) / printScaleFactor,
    y: (top - centerCoords.y) / printScaleFactor
  };
};

export function validDimensions(rect) {
  if (
    rect?.width === null ||
    rect?.height === null ||
    isNaN(rect?.width) ||
    isNaN(rect?.height)
  ) {
    return false;
  }
  return true;
}

export function validConstraintsPixels(rect) {
  if (
    rect?.left === null ||
    rect?.right === null ||
    rect?.top === null ||
    rect?.bottom === null ||
    isNaN(rect?.left) ||
    isNaN(rect?.right) ||
    isNaN(rect?.top) ||
    isNaN(rect?.bottom)
  ) {
    return false;
  }
  return true;
}

export function validConstraintsInches(border) {
  if (
    border?.min?.x === null ||
    border?.max?.x === null ||
    border?.min?.y === null ||
    border?.max?.y === null ||
    isNaN(border?.min?.x) ||
    isNaN(border?.max?.x) ||
    isNaN(border?.min?.y) ||
    isNaN(border?.max?.y)
  ) {
    return false;
  }
  return true;
}

export function constraintsMeaningfullyDifferent({
  newConstraintsInfo,
  oldConstraintsInfo
}) {
  if (!newConstraintsInfo || !oldConstraintsInfo) return true;
  return (
    Math.abs(newConstraintsInfo?.areaInches - oldConstraintsInfo?.areaInches) >
    newConstraintsInfo?.areaInches * 0.5
  );
}

export function scaledDesignFitsNewConstraints({
  designDimensions,
  scaledConstraintsDimensions,
  ratio
}) {
  if (
    !validDimensions(designDimensions) &&
    !validDimensions(scaledConstraintsDimensions)
  ) {
    return false;
  }
  return (
    scaledConstraintsDimensions.width >= designDimensions.width * ratio &&
    scaledConstraintsDimensions.height >= designDimensions.height * ratio
  );
}

export function getDimensionsInches(constraintsInfo) {
  const dimensions = {
    width: constraintsInfo?.deltaXInches,
    height: constraintsInfo?.deltaYInches
  };
  return validDimensions(dimensions) ? dimensions : null;
}

export function getOffsetDesignCenter({designInfo, ratio = 1}) {
  return {
    x: (designInfo?.center?.pixels?.x || 0) * ratio,
    y: (designInfo?.center?.pixels?.y || 0) * ratio
  };
}

export function getOffsetGuidelineCenter({
  guidelineInfo,
  newConstraintsInfo,
  newScale = 1
}) {
  const offsetCenterX =
    newConstraintsInfo?.center?.pixels?.x - guidelineInfo?.center?.pixels?.x ||
    0;
  const offsetCenterY =
    newConstraintsInfo?.center?.pixels?.y - guidelineInfo?.center?.pixels?.y ||
    0;
  return {
    x: offsetCenterX / newScale,
    y: offsetCenterY / newScale
  };
}

export function getRatioForNInches({
  fullDimensionInches,
  fullDimensionRatio,
  desiredValInches
}) {
  const ratio = fullDimensionRatio * (desiredValInches / fullDimensionInches);
  return isNaN(ratio) ? 1 : ratio;
}

export function getConstraintsScaleRatio({
  newConstraint,
  oldConstraint,
  dimension
}) {
  if (
    !validDimensions(newConstraint) ||
    !validDimensions(oldConstraint) ||
    !newConstraint[dimension] ||
    !oldConstraint[dimension]
  ) {
    return 1;
  }
  return newConstraint[dimension] / oldConstraint[dimension];
}

export function getConstraintsDesignScaleRatio({
  constraintsDimensions,
  designDimensions,
  dimension,
  dimensionPercentage
}) {
  if (
    !validDimensions(constraintsDimensions) ||
    !validDimensions(designDimensions) ||
    !constraintsDimensions[dimension] ||
    !designDimensions[dimension]
  ) {
    return 1;
  }
  const percentage = dimensionPercentage || 1;
  return (
    (constraintsDimensions[dimension] / designDimensions[dimension]) *
    percentage
  );
}
